import React, { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { createGTMTracking, getGTMAttributes } from '../../utils/gtm/gtm-tracking';
import { getCustomPlanFeatures, getLocalizedPricingPlans } from './landingContent';
import {
  CurrencyCode,
  formatPrice,
  formatResumePrice,
  formatTalentSearchPrice,
  formatTalentUnlockPrice,
  SUPPORTED_CURRENCIES,
} from '../../utils/currency';
import { formatCurrency as formatCurrencyAmount } from '../../utils/locale';
import { PricingCard } from './PricingCard';
import Button from '../ui/atoms/Button';
import { SegmentedButton } from '../ui/atoms/SegmentedButton';
import { fireMetaPixelCustomEvent } from '../../services/tracking.service';

interface PricingSectionProps {
  className?: string;
}

export const PricingSection: React.FC<PricingSectionProps> = ({ className }) => {
  const { t } = useTranslation('marketing');
  const navigate = useNavigate();
  const pricingPlans = useMemo(() => getLocalizedPricingPlans(t), [t]);
  const customPlanFeatures = useMemo(() => getCustomPlanFeatures(t), [t]);
  const [currency, setCurrency] = useState<CurrencyCode>('USD');
  const [isSubmitting] = useState(false);
  const [selectedPaidPack, setSelectedPaidPack] = useState<string>('starter');
  const switchPlanRef = useRef<HTMLButtonElement>(null);

  const handlePlanChange = (packCode: string) => {
    setSelectedPaidPack(packCode);
    switchPlanRef.current?.setAttribute('data-target', packCode);
    switchPlanRef.current?.click();
  };

  const handleButtonClick = (packCode: string) => {
    if (packCode === 'free_trial') {
      fireMetaPixelCustomEvent('FreeTrialClick', { pack_code: packCode });
    }
    // Land on /signup with the chosen plan + currency — the SignupPage
    // reads these off the query string, persists them through the auth
    // flow, and the /api/onboarding/finalize call uses currency to pick
    // the payment provider (MAD → Berexia, USD/EUR → Stripe).
    navigate(
      `/signup?plan=${encodeURIComponent(packCode)}&currency=${encodeURIComponent(currency)}`,
    );
  };

  const handleCustomPlanClick = () => {
    window.open('https://calendly.com/aida-invirtus/30min', '_blank');
  };

  const formattedPrices = useMemo(() => {
    return pricingPlans.reduce<Record<string, string>>((acc, plan) => {
      acc[plan.packCode] = formatPrice(plan.baseAmount, currency, plan.eurAmount);
      return acc;
    }, {});
  }, [currency, pricingPlans]);

  const monthlyFormattedPrices = useMemo(() => {
    return pricingPlans.reduce<Record<string, string>>((acc, plan) => {
      const monthlyUsdAmount = plan.baseAmount / 12;
      const monthlyEurAmount = plan.eurAmount !== undefined ? plan.eurAmount / 12 : undefined;
      const monthlyAmount =
        currency === 'EUR' && monthlyEurAmount !== undefined
          ? monthlyEurAmount
          : monthlyUsdAmount * (currency === 'MAD' ? 10 : 1);
      const roundedUpMonthlyAmount = Math.ceil(monthlyAmount);

      acc[plan.packCode] = formatCurrencyAmount(roundedUpMonthlyAmount, currency);
      return acc;
    }, {});
  }, [currency, pricingPlans]);

  const freePlan = pricingPlans.find(p => p.packCode === 'free_trial');
  const paygPlan = pricingPlans.find(p => p.packCode === 'payg');

  // Surface the per-resume screening, per-talent-search, and per-talent-unlock
  // rates (currency-aware) as subtle extra bullets in the PAYG card details,
  // without disturbing the price/description.
  const paygPlanWithResume = useMemo(() => {
    if (!paygPlan) return paygPlan;
    return {
      ...paygPlan,
      features: [
        ...paygPlan.features,
        { text: t('pricing.resume_price', { price: formatResumePrice(currency) }) },
        { text: t('pricing.talent_search_price', { price: formatTalentSearchPrice(currency) }) },
        { text: t('pricing.talent_unlock_price', { price: formatTalentUnlockPrice(currency) }) },
      ],
    };
  }, [paygPlan, currency, t]);

  const paidPlans = pricingPlans.filter(p => ['starter', 'core', 'pro'].includes(p.packCode));
  const currentPaidPlan = pricingPlans.find(p => p.packCode === selectedPaidPack) || paidPlans[1];

  const handleCurrencyChange = (newCurrency: CurrencyCode) => {
    setCurrency(newCurrency);
  };

  return (
    <section
      id="pricing"
      className={`flex flex-col items-center gap-8 sm:gap-10 lg:gap-[50px] w-full bg-white px-4 sm:px-6 lg:px-0 py-8 sm:py-12 lg:py-[60px] ${className || ''}`}
      {...getGTMAttributes(createGTMTracking('SECTION_VIEWED', { origin: 'landing_page', target: 'pricing' }))}
    >
      <div className="flex flex-col items-center gap-4 w-full max-w-4xl px-4 text-center">
        <h2 className="text-dark-invirtus text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight tracking-tight">
          {t('pricing.title')}
        </h2>
        <p className="text-[#4C4E51] text-base sm:text-lg max-w-2xl">
          {t('pricing.subtitle')}
        </p>
        
        {/* Currency switcher — SegmentedButton (rounded-full raised track). */}
        <SegmentedButton
          aria-label={t('pricing.currency_aria_label', { defaultValue: 'Currency' })}
          className="mt-4"
          size="md"
          appearance="raised"
          intent="secondary"
          value={currency}
          onValueChange={(next) => handleCurrencyChange(next as CurrencyCode)}
          options={SUPPORTED_CURRENCIES.map((curr) => ({
            value: curr,
            label: curr,
            dataAttributes: getGTMAttributes(
              createGTMTracking('SWITCH_CURRENCY', {
                origin: 'pricing_section',
                target: curr,
              }),
            ),
          }))}
        />
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5 lg:gap-5 w-full max-w-[1400px] px-4 lg:px-8 items-stretch">
        {/* 1. Free Trial */}
        <div className="free-trial-card flex">
            {freePlan && (
              <PricingCard
                plan={freePlan}
                formattedPrice={formattedPrices[freePlan.packCode] ?? '$0'}
                isHighlighted={false}
                isSubmitting={isSubmitting}
                onClick={() => handleButtonClick(freePlan.packCode)}
                gtmAttributes={getGTMAttributes(createGTMTracking('PLAN_SELECTED', { origin: 'pricing_card', target: freePlan.packCode }))}
                gtmAttributesSecondary={getGTMAttributes(createGTMTracking('SIGNUP', { origin: 'pricing_card' }))}
              />
            )}
        </div>

        {/* 2. Pay as you go — hidden for MAD (no card saving with Berexia) */}
        <div className={`flex ${currency === 'MAD' ? 'opacity-40 pointer-events-none' : ''}`}>
          {paygPlanWithResume && (
            <PricingCard
              plan={paygPlanWithResume}
              formattedPrice={formattedPrices[paygPlanWithResume.packCode] ?? ''}
              isHighlighted={false}
              isSubmitting={isSubmitting}
              onClick={currency === 'MAD' ? undefined : () => handleButtonClick(paygPlanWithResume.packCode)}
              gtmAttributes={getGTMAttributes(createGTMTracking('PLAN_SELECTED', { origin: 'pricing_card', target: 'payg' }))}
              gtmAttributesSecondary={getGTMAttributes(createGTMTracking('SIGNUP', { origin: 'pricing_card' }))}
            />
          )}
        </div>

        {/* 3. Packs (switcher) */}
        <div className="flex">
            <PricingCard
              plan={currentPaidPlan}
              formattedPrice={currentPaidPlan ? monthlyFormattedPrices[currentPaidPlan.packCode] : ''}
              priceSubtextOverride={t('pricing.per_month')}
              secondaryCadenceLabel={t('pricing.billed_yearly')}
              annualCommitmentBadgeLabel={t('pricing.annual_commitment_badge')}
              secondaryPriceLabel={
                currentPaidPlan
                  ? formattedPrices[currentPaidPlan.packCode]
                  : undefined
              }
              isHighlighted={true}
              isSubmitting={isSubmitting}
              onClick={currentPaidPlan ? () => handleButtonClick(currentPaidPlan.packCode) : undefined}
              showPlanSelector={true}
              availablePlans={paidPlans}
              selectedPlanCode={selectedPaidPack}
              onPlanChange={handlePlanChange}
              gtmAttributes={getGTMAttributes(createGTMTracking('PLAN_SELECTED', { origin: 'pricing_card', target: currentPaidPlan?.packCode ?? 'core' }))}
              gtmAttributesSecondary={getGTMAttributes(createGTMTracking('SIGNUP', { origin: 'pricing_card' }))}
            />
            <Button
              ref={switchPlanRef}
              aria-hidden
              tabIndex={-1}
              className="sr-only"
              {...getGTMAttributes(createGTMTracking('SWITCH_PLAN', { origin: 'pricing_card', target: selectedPaidPack }))}
            />
        </div>

        {/* Enterprise / Custom */}
        <div className="flex min-w-0">
            <PricingCard
              isCustom={true}
              isHighlighted={false}
              customLabels={{
                planName: t('pricing.custom.name'),
                price: t('pricing.custom.price'),
                description: t('pricing.custom.description'),
                buttonText: t('pricing.custom.button'),
                features: customPlanFeatures,
              }}
              onClick={handleCustomPlanClick}
              gtmAttributes={getGTMAttributes(createGTMTracking('PLAN_SELECTED', { origin: 'pricing_card', target: 'custom' }))}
              gtmAttributesSecondary={getGTMAttributes(createGTMTracking('REQUEST_DEMO', { origin: 'pricing_card' }))}
            />
        </div>
      </div>
    </section>
  );
};
